import React, { useEffect, useState } from 'react';

import { getExpandedNavigationIds } from '../application/getExpandedNavigationIds';
import {
    LocalNavigationTarget,
    NavigationItem,
    NavigationSection,
    NavigationTarget,
} from '../domain/navigation';
import {
    NavigationPluginSlot,
    NavigationShellPlugin,
} from './NavigationPluginSlot';
import './navigation-shell.css';

const MOBILE_NAVIGATION_MEDIA_QUERY = '(max-width: 991px)';

const useIsMobileNavigation = () => {
    const [isMobileNavigation, setIsMobileNavigation] = useState(
        () =>
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia(MOBILE_NAVIGATION_MEDIA_QUERY).matches,
    );

    useEffect(() => {
        if (
            typeof window === 'undefined' ||
            typeof window.matchMedia !== 'function'
        ) {
            return undefined;
        }

        const mediaQuery = window.matchMedia(MOBILE_NAVIGATION_MEDIA_QUERY);
        const updateIsMobileNavigation = () =>
            setIsMobileNavigation(mediaQuery.matches);
        updateIsMobileNavigation();
        mediaQuery.addEventListener?.('change', updateIsMobileNavigation);

        return () =>
            mediaQuery.removeEventListener?.(
                'change',
                updateIsMobileNavigation,
            );
    }, []);

    return isMobileNavigation;
};

export type NavigationShellBrand = {
    name: string;
    logoUrl?: string | null;
    homeTarget?: NavigationTarget;
};

export interface NavigationShellProps {
    brand: NavigationShellBrand;
    sections: NavigationSection[];
    children: React.ReactNode;
    activeItemId?: string;
    accentColor?: string | null;
    plugins?: NavigationShellPlugin[];
    defaultSidebarCollapsed?: boolean;
    renderIcon?: (icon: string) => React.ReactNode;
    renderLocalLink?: (
        target: LocalNavigationTarget,
        children: React.ReactNode,
        item: NavigationItem,
    ) => React.ReactNode;
    onNavigate?: (
        event: React.MouseEvent<HTMLAnchorElement>,
        item: NavigationItem,
    ) => void;
}

type NavigationLinkProps = {
    item: NavigationItem;
    activeItemId?: string;
    renderLocalLink?: NavigationShellProps['renderLocalLink'];
    onNavigate?: NavigationShellProps['onNavigate'];
    onSelected: () => void;
};

const NavigationLink = ({
    item,
    activeItemId,
    renderLocalLink,
    onNavigate,
    onSelected,
}: NavigationLinkProps) => {
    if (!item.target) {
        return <span>{item.label}</span>;
    }

    const content = (
        <>
            <span>{item.label}</span>
            {item.badge ? (
                <span className="opus-navigation-shell__badge">
                    {item.badge}
                </span>
            ) : null}
        </>
    );
    const className =
        item.id === activeItemId
            ? 'opus-navigation-shell__link opus-navigation-shell__link--active'
            : 'opus-navigation-shell__link';

    if (item.target.kind === 'local' && renderLocalLink) {
        return (
            <span
                className={className}
                aria-current={item.id === activeItemId ? 'page' : undefined}
            >
                {renderLocalLink(item.target, content, item)}
            </span>
        );
    }

    const href =
        item.target.kind === 'local' ? item.target.to : item.target.href;

    return (
        <a
            className={className}
            href={href}
            aria-current={item.id === activeItemId ? 'page' : undefined}
            onClick={(event) => {
                onSelected();
                onNavigate?.(event, item);
            }}
        >
            {content}
        </a>
    );
};

type NavigationMenuItemProps = NavigationLinkProps & {
    item: NavigationItem;
    expanded: boolean;
    sidebarCollapsed: boolean;
    renderIcon?: (icon: string) => React.ReactNode;
    onToggle: (itemId: string) => void;
};

const NavigationMenuItem = ({
    item,
    expanded,
    sidebarCollapsed,
    renderIcon,
    onToggle,
    ...linkProps
}: NavigationMenuItemProps) => {
    const hasChildren = Boolean(item.children?.length);
    const showExpandedState = !sidebarCollapsed && expanded;

    return (
        <li className="opus-navigation-shell__item">
            {hasChildren ? (
                <button
                    type="button"
                    className={
                        showExpandedState
                            ? 'opus-navigation-shell__group opus-navigation-shell__group--expanded'
                            : 'opus-navigation-shell__group'
                    }
                    aria-label={item.label}
                    aria-expanded={sidebarCollapsed ? undefined : expanded}
                    aria-controls={`navigation-children-${item.id}`}
                    onClick={() => {
                        if (!sidebarCollapsed) {
                            onToggle(item.id);
                        }
                    }}
                >
                    {item.icon && renderIcon ? renderIcon(item.icon) : null}
                    <span className="opus-navigation-shell__group-label">
                        {item.label}
                    </span>
                    <span
                        className="opus-navigation-shell__chevron"
                        aria-hidden="true"
                    >
                        ›
                    </span>
                </button>
            ) : (
                <NavigationLink item={item} {...linkProps} />
            )}

            {hasChildren ? (
                <ul
                    id={`navigation-children-${item.id}`}
                    className="opus-navigation-shell__children"
                    {...(!sidebarCollapsed && !expanded
                        ? { hidden: true }
                        : {})}
                >
                    {item.children?.map((child) => (
                        <li
                            className="opus-navigation-shell__child"
                            key={child.id}
                        >
                            <NavigationLink item={child} {...linkProps} />
                        </li>
                    ))}
                </ul>
            ) : null}
        </li>
    );
};

export const NavigationShell = ({
    brand,
    sections,
    children,
    activeItemId,
    accentColor = '#d32f2f',
    plugins = [],
    defaultSidebarCollapsed = false,
    renderIcon,
    renderLocalLink,
    onNavigate,
}: NavigationShellProps) => {
    const [expandedItemIds, setExpandedItemIds] = useState(
        () => new Set(getExpandedNavigationIds(sections, activeItemId)),
    );
    const [sidebarCollapsed, setSidebarCollapsed] = useState(
        defaultSidebarCollapsed,
    );
    const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
    const isMobileNavigation = useIsMobileNavigation();
    const navigationItemsCollapsed = sidebarCollapsed && !isMobileNavigation;

    const toggleExpanded = (itemId: string) => {
        setExpandedItemIds((current) => {
            const next = new Set(current);
            if (next.has(itemId)) {
                next.delete(itemId);
            } else {
                next.add(itemId);
            }
            return next;
        });
    };

    const brandContent = brand.logoUrl ? (
        <img
            className="opus-navigation-shell__logo"
            src={brand.logoUrl}
            alt={`${brand.name} logo`}
        />
    ) : (
        <span className="opus-navigation-shell__brand-name">{brand.name}</span>
    );

    const brandItem: NavigationItem = {
        id: 'navigation-home',
        label: brand.name,
        target: brand.homeTarget,
    };
    const brandLink = (() => {
        if (!brand.homeTarget) {
            return brandContent;
        }

        if (brand.homeTarget.kind === 'local' && renderLocalLink) {
            return renderLocalLink(brand.homeTarget, brandContent, brandItem);
        }

        const href =
            brand.homeTarget.kind === 'local'
                ? brand.homeTarget.to
                : brand.homeTarget.href;
        return (
            <a
                className="opus-navigation-shell__brand-link"
                href={href}
                onClick={(event) => onNavigate?.(event, brandItem)}
            >
                {brandContent}
            </a>
        );
    })();

    return (
        <div
            className={[
                'opus-navigation-shell',
                sidebarCollapsed ? 'opus-navigation-shell--collapsed' : '',
                mobileNavigationOpen
                    ? 'opus-navigation-shell--mobile-open'
                    : '',
            ]
                .filter(Boolean)
                .join(' ')}
            style={
                {
                    '--opus-navigation-accent': accentColor || '#d32f2f',
                } as React.CSSProperties
            }
        >
            <header className="opus-navigation-shell__topbar">
                <div className="opus-navigation-shell__brand">{brandLink}</div>

                <button
                    type="button"
                    id="toggle-menu-btn"
                    className="opus-navigation-shell__desktop-toggle"
                    aria-label="Toggle sidebar width"
                    aria-pressed={sidebarCollapsed}
                    onClick={() =>
                        setSidebarCollapsed((collapsed) => !collapsed)
                    }
                >
                    <i className="fal fa-bars icon-md-lg" aria-hidden="true" />
                </button>

                <div className="opus-navigation-shell__plugins">
                    {plugins.map((plugin) => (
                        <NavigationPluginSlot key={plugin.id} plugin={plugin} />
                    ))}
                </div>

                <button
                    type="button"
                    className="opus-navigation-shell__mobile-toggle"
                    aria-label={
                        mobileNavigationOpen
                            ? 'Close navigation menu'
                            : 'Open navigation menu'
                    }
                    aria-expanded={mobileNavigationOpen}
                    onClick={() => setMobileNavigationOpen((isOpen) => !isOpen)}
                >
                    <i className="fal fa-bars icon-md-lg" aria-hidden="true" />
                </button>
            </header>

            <button
                type="button"
                className="opus-navigation-shell__backdrop"
                aria-label="Close navigation menu"
                onClick={() => setMobileNavigationOpen(false)}
            />

            <nav
                className="opus-navigation-shell__sidebar"
                aria-label="Primary navigation"
            >
                <div className="opus-navigation-shell__sidebar-content">
                    {sections.map((section) => (
                        <section
                            className="opus-navigation-shell__section"
                            key={section.id}
                        >
                            <h2 className="opus-navigation-shell__section-title">
                                {section.label}
                            </h2>
                            <ul className="opus-navigation-shell__list">
                                {section.items.map((item) => (
                                    <NavigationMenuItem
                                        key={item.id}
                                        item={item}
                                        activeItemId={activeItemId}
                                        expanded={expandedItemIds.has(item.id)}
                                        sidebarCollapsed={
                                            navigationItemsCollapsed
                                        }
                                        renderIcon={renderIcon}
                                        renderLocalLink={renderLocalLink}
                                        onNavigate={onNavigate}
                                        onSelected={() =>
                                            setMobileNavigationOpen(false)
                                        }
                                        onToggle={toggleExpanded}
                                    />
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </nav>

            <main className="opus-navigation-shell__main">{children}</main>
        </div>
    );
};

export default NavigationShell;
